import React, { useCallback, useState } from 'react';
import { Modal, message } from 'antd';
import styled from '@emotion/styled';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FormGenerateMultiCode } from './form';
import { ExportToCSV } from '../../molecules/export/ExportToCSV';
import { TitleModal } from '../../molecules/modal/TitleModal';
import { ACCESS_CODE } from '../../../constants/define/translation';
import { appStyles } from '../../../styles';
import { AccessCodeStatus, SubscriptionDurationValue, SubscriptionFormGenerateMulti } from '../../../types/accessCode';
import { generateMultiCode } from '../../../repository/mytm/access-code/accessCodeRepository';
import { fileNameCsv, toAccessCodeListCsv } from '../../../utils/transfer/access-code';

interface AccessCodeModalProps {
  isOpenModal: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

export const AccessCodeModal = (props: AccessCodeModalProps) => {
  const { t } = useTranslation(ACCESS_CODE);
  const [csvData, setCsvData] = useState<any[]>();
  const { isOpenModal, onClose, onRefresh } = props;

  const onSetCsvData = useCallback((data: any) => {
    setCsvData(undefined);
  }, []);

  const onGenerateMulti = useCallback(
    async (values: SubscriptionFormGenerateMulti, onSuccess: () => void, onFailed: () => void) => {
      try {
        const dataGenerate = await generateMultiCode({
          quantity: Number(values.quantity),
          plan: Number(values.subscriptionPlan),
          duration:
            Number(values.subscriptionDuration) === SubscriptionDurationValue.RANDOM
              ? SubscriptionDurationValue.RANDOM
              : Number(values?.inputDuration),
          target: values.target,
        });
        onClose();
        const toAccessCodeList = toAccessCodeListCsv(dataGenerate.data, AccessCodeStatus.AVAILABLE);
        setCsvData(toAccessCodeList);
        onRefresh();
        onSuccess();
        message.success(t('message.successGenerateOnly'));
      } catch (error) {
        onFailed();
        message.error(t('message.errorGenerateOnly'));
      }
    },
    [],
  );

  return (
    <StyledModal
      title={<TitleModal title={t('header.generate')} />}
      centered
      visible={isOpenModal}
      width={630}
      footer={null}
      onCancel={() => {
        onClose();
      }}
      maskClosable={false}
      closeIcon={<CloseCircleOutlined />}
      forceRender
    >
      <FormGenerateMultiCode onGenerate={onGenerateMulti} />
      <ExportToCSV data={csvData} fileName={fileNameCsv()} onSetCsvData={onSetCsvData} />
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  & > .ant-modal-content {
    ${appStyles.commonModalContentStyle}
  }

  .ant-modal-header {
    padding-left: 0;
  }

  .btn-wrap {
    text-align: center;
  }

  .btn-generate-code {
    width: 270px;
    margin: 2rem;
  }

  .ant-form-item-label {
    font-weight: bold;
  }

  .form-content {
    padding: 1.5rem 1.5rem 3.5rem;
  }

  .btn-submit-generate-access-code {
    position: absolute;
    bottom: 1rem;
    right: 3rem;
  }

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  .title-primary {
    padding: 0.4rem;
  }

  .ant-form-item {
    margin-bottom: 8px;
  }

  .input-duration {
    margin-top: -20px;
    margin-bottom: 0;
  }

  .input-invisibility {
    visibility: hidden;
  }
`;
